// Pure capability filter for the sales daily checklist.
//
// Slice RP-2b-i of docs/planning/firefly_role_context_presentation.md
// §10. The server's `/api/sales/daily-checklist` endpoint returns a
// uniform item set + `fraction { done, total }` for every sales
// caller. The `ad_logged` item contributes a weighted sub-count
// (`SALES_ADS_DAILY_SUBITEMS` = 6) to that fraction. When the user
// is not an ads-runner — either because they don't hold the sales
// role at all (Dean's owner-only case) or because they've toggled
// `is_ads_runner` off (RP-2b-ii UI lands later) — we filter the
// `ad_logged` item out of the rendered list and subtract its
// `ad_progress.done` / `ad_progress.target` from the fraction so
// the displayed percentage reflects only the tasks they're
// responsible for.
//
// This is a client-side patch, not a server change. The server
// continues to compute the full org-policy checklist; the client
// just hides the ads piece for non-ads-runners. Two reasons:
//
//   1. Capabilities are a client-resolved primitive in RP-2b-i
//      (localStorage override + role-derived default). Routing the
//      capability to the server would require an API change + a
//      DB-backed source — that's RP-2c work, not this slice.
//   2. The checklist's per-item `done` state still gets persisted
//      server-side; nothing about toggling `is_ads_runner` should
//      cause a sales rep's manual-item completion history to be
//      reinterpreted differently on the server.
//
// Tests live in `tests/salesDailyChecklistFilter.test.ts`.

import type { SalesDailyChecklistItem } from "@/lib/salesDailyChecklistClient";

/**
 * The stable id of the ads-runner checklist item. Mirrors
 * `SALES_DAILY_CHECKLIST_ITEM_ORDER` in `salesDailyChecklistItems.ts`
 * and `SALES_ADS_DAILY_SUBITEMS` in
 * `server/admin-handlers/salesChecklistConstants.ts`. Centralized
 * here so the filter is the single client-side authority on "what
 * counts as an ads item."
 */
export const ADS_RUNNER_CHECKLIST_ITEM_ID = "ad_logged";

/**
 * Returns the input items with the ads-runner item removed when the
 * caller is not an ads-runner. When they ARE an ads-runner, the
 * input is returned unchanged (reference-equal) so React keys stay
 * stable for re-renders.
 */
export function filterChecklistItemsForCapability(
  items: readonly SalesDailyChecklistItem[],
  isAdsRunner: boolean,
): readonly SalesDailyChecklistItem[] {
  if (isAdsRunner) return items;
  return items.filter((it) => it.id !== ADS_RUNNER_CHECKLIST_ITEM_ID);
}

/**
 * Adjusts the server-reported fraction to exclude the ads item's
 * contribution when the user is not an ads-runner. When they ARE
 * an ads-runner the input is returned unchanged (reference-equal).
 *
 * Edge cases:
 *   - `ad_logged` item missing or missing `ad_progress`: fraction
 *     returned unchanged. The server may have already excluded the
 *     ads item (defensive — we don't double-subtract).
 *   - `ad_progress.done > fraction.done` (data anomaly): clamps
 *     result to 0 so a corrupted source never makes the percentage
 *     go negative.
 *   - `total === 0` after adjustment: returns `{ done: 0, total: 0 }`
 *     unchanged in shape; the widget's `percentFromFraction` already
 *     handles the empty-total case.
 */
export function adjustChecklistFractionForCapability(
  fraction: { done: number; total: number },
  items: readonly SalesDailyChecklistItem[],
  isAdsRunner: boolean,
): { done: number; total: number } {
  if (isAdsRunner) return fraction;
  const ad = items.find((it) => it.id === ADS_RUNNER_CHECKLIST_ITEM_ID);
  const adProgress = ad?.ad_progress;
  if (!adProgress) return fraction;
  return {
    done: Math.max(0, fraction.done - adProgress.done),
    total: Math.max(0, fraction.total - adProgress.target),
  };
}

/**
 * Recompute the `all_done` flag from the post-filter item list. The
 * server's `all_done` is computed over the full server-side item
 * set; once we strip the ads item, the local definition is "every
 * non-ads item is done." When the user IS an ads-runner the server's
 * value is preserved verbatim.
 */
export function recomputeAllDoneForCapability(
  items: readonly SalesDailyChecklistItem[],
  serverAllDone: boolean,
  isAdsRunner: boolean,
): boolean {
  if (isAdsRunner) return serverAllDone;
  const filtered = items.filter(
    (it) => it.id !== ADS_RUNNER_CHECKLIST_ITEM_ID,
  );
  if (filtered.length === 0) return false;
  return filtered.every((it) => it.done);
}
